import { Box, type SxProps, type Theme } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { fadeInImageSx, shimmerSx } from "./shimmer";
import type { TintPalette } from "@/utils/coverTint";
import { parseShiftPercent, useVinylReveal, vinylMotion, type VinylPeek } from "@/hooks/useVinylReveal";
import { sharedProps } from "@/utils/sharedElements";

export { VINYL_HOST_CLASS } from "@/hooks/useVinylReveal";

const SLIDE_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const TURN_EASE = "cubic-bezier(0.34, 1.32, 0.64, 1)";

interface VinylCoverProps {
  src: string;
  alt: string;
  srcSet?: string;
  sizes?: string;
  labelSrc?: string;
  playing?: boolean;
  peek?: VinylPeek;
  tint?: TintPalette | null;
  radius?: string;
  shift?: string;
  priority?: boolean;
  /** Marks the sleeve as a shared element for page transitions (see sharedElements.ts). */
  sharedKey?: string;
  onImageLoad?: () => void;
  className?: string;
  sx?: SxProps<Theme>;
}

const GROOVES =
  "radial-gradient(circle at 50% 50%, transparent 0 31%, rgba(255,255,255,0.05) 31.5% 32%, transparent 32.5% 100%), repeating-radial-gradient(circle at 50% 50%, #111 0 1.2%, #1c1c1c 1.4% 1.9%, #0d0d0d 2.1% 2.6%)";

const SHEEN =
  "conic-gradient(from 215deg, transparent 0deg, rgba(255,255,255,0.1) 16deg, transparent 48deg, transparent 180deg, rgba(255,255,255,0.07) 196deg, transparent 228deg)";

export function VinylCover({
  src,
  alt,
  srcSet,
  sizes,
  labelSrc,
  playing = false,
  peek = "hover",
  tint,
  radius = "10px",
  shift = "17%",
  priority = false,
  sharedKey,
  onImageLoad,
  className,
  sx,
}: VinylCoverProps) {
  const [loaded, setLoaded] = useState(false);
  const markLoaded = useCallback(() => {
    setLoaded(true);
    onImageLoad?.();
  }, [onImageLoad]);
  const imageRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (node?.complete && node.naturalWidth > 0) markLoaded();
    },
    [markLoaded],
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { state, reduced } = useVinylReveal(rootRef, { peek, playing });
  const motion = vinylMotion(parseShiftPercent(shift), peek, reduced);
  const out = state === "out";
  const glowShadow = tint
    ? `0 24px 46px -20px color-mix(in srgb, ${tint.glow} 70%, transparent), 0 10px 24px rgba(0,0,0,0.34)`
    : "0 10px 26px rgba(0,0,0,0.34)";

  return (
    <Box
      ref={rootRef}
      className={["vinyl-cover", className].filter(Boolean).join(" ")}
      data-playing={playing ? "true" : "false"}
      data-peek={peek}
      data-reveal={state}
      sx={[
        {
          position: "relative",
          width: "100%",
          aspectRatio: "1 / 1",
          isolation: "isolate",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        className="vinyl-track"
        aria-hidden
        style={{
          transform: `translateX(${out ? motion.outShift : motion.restShift}%)`,
          opacity: out ? motion.outOpacity : motion.restOpacity,
        }}
        sx={{
          position: "absolute",
          top: "4%",
          left: "4%",
          width: "92%",
          height: "92%",
          zIndex: 0,
          borderRadius: "50%",
          boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
          willChange: "transform",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            pointerEvents: "none",
            background: SHEEN,
            WebkitMaskImage: "radial-gradient(circle, transparent 0 33%, #000 34%)",
            maskImage: "radial-gradient(circle, transparent 0 33%, #000 34%)",
          },
          transition: `transform 0.95s ${SLIDE_EASE}, opacity 0.3s ease`,
          "@media (prefers-reduced-motion: reduce)": { transition: "opacity 0.3s ease" },
        }}
      >
        <Box
          className="vinyl-turn"
          style={{ transform: `rotate(${out ? motion.outTurn : motion.restTurn}deg)` }}
          sx={{
            width: "100%",
            height: "100%",
            transition: `transform 1.15s ${TURN_EASE}`,
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          <Box
            className="vinyl-disc"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background: GROOVES,
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
              position: "relative",
              "@keyframes vinylSpin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
              animation: playing ? "vinylSpin 2.6s linear infinite" : "none",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: "34%",
                borderRadius: "50%",
                backgroundImage: `url(${labelSrc || src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                boxShadow: "0 0 0 2px rgba(0,0,0,0.5)",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: "44%",
                  borderRadius: "50%",
                  backgroundColor: "#0b0b0b",
                },
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box
        className="vinyl-sleeve"
        {...sharedProps("art", sharedKey)}
        data-loaded={loaded || !src ? "true" : "false"}
        sx={{
          ...shimmerSx,
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          borderRadius: radius,
          overflow: "hidden",
          backgroundColor: tint?.surface || "rgba(0,0,0,0.25)",
          boxShadow: glowShadow,
        }}
      >
        <Box
          component="img"
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
          ref={imageRef}
          onLoad={markLoaded}
          onError={() => setLoaded(true)}
          sx={{
            ...fadeInImageSx,
            position: "relative",
            zIndex: 1,
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </Box>
    </Box>
  );
}

export default VinylCover;
